import React, { useEffect } from 'react';
import { Button, Box, Typography, Paper, Stack, Alert } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { styled, alpha } from '@mui/material/styles';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import { FileUploadStyles, SecondaryButton } from './Styles';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileList from './ListItem';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const VisuallyHiddenInput = styled('input')(({ theme }) => ({
  ...FileUploadStyles(tokens(theme.palette.mode)).hiddenInput
}));

const UploadIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2)
}));

/**
 * Reusable file upload component
 * @param {Object} props Component props
 * @param {File|File[]} props.selectedFiles The currently selected file(s)
 * @param {Function} props.setSelectedFiles Function to set the selected file(s)
 * @param {Function} props.onFileSelect Callback when file(s) are selected
 * @param {string} props.label Label for the upload area
 * @param {boolean} props.isRequired Whether a file is required
 * @param {string} props.requiredMessage Message to show when file is required
 * @param {string} props.description Description text for the file upload
 * @param {string} props.acceptedFormats Text to show for accepted formats
 * @param {Object} props.customIcon Custom icon to use
 * @param {string} props.browseButtonLabel Label for the browse button
 * @param {string} props.removeButtonLabel Label for the remove file button
 * @param {string} props.uploadedFileLabel Label for the uploaded file
 * @param {string} props.uploadedFileTooltip Tooltip for the uploaded file
 * @param {boolean} props.multiple Whether to allow multiple file selection
 */
const FileUpload = ({
  selectedFiles = [],
  setSelectedFiles,
  onFileSelect,
  label = '',
  isRequired = false,
  requiredMessage = '',
  description = '',
  acceptedFormats = '',
  customIcon = null,
  browseButtonLabel,
  removeButtonLabel,
  uploadedFileLabel,
  uploadedFileTooltip,
  multiple = false
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const styles = FileUploadStyles(colors);

  // French translations for text
  const translations = {
    file_upload_required: "Veuillez télécharger un fichier pour continuer",
    file_upload_label: "Drag & drop",
    menu: {
      file_upload_browse: "browse files"
    },
    file_upload_drag_active: "Déposez le fichier ici",
    file_upload_description: "images, videos, or any file",
    file_upload_accepted_formats: "or browse files on your computer"
  };
// useEffect(()=>{
//   console.log("selected files",selectedFiles)
// },[selectedFiles])
  const handleUpload = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      const processedFiles = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];
      
      const newFiles = processedFiles.map(file => {
        const fileType = (file.type || '').split('/')[0];
        const fileExtension = (file.name || '').split('.').pop().toLowerCase();

        let type = 'pdf';  // Default to document-like
        if (fileType === 'image') type = 'image';
        else if (fileType === 'video') type = 'video';
        else if (['pdf'].includes(fileExtension)) type = 'pdf';
        else if (['doc', 'docx'].includes(fileExtension)) type = 'docx';
        else if (['ppt', 'pptx'].includes(fileExtension)) type = 'pptx';
        else if (['xls', 'xlsx'].includes(fileExtension)) type = 'xlsx';
        else if (['zip', 'rar', '7z'].includes(fileExtension)) type = 'zip';

        return {
          name: file.name,
          type: type,
          date: new Date().toISOString()?.split('T')[0],
          file: file
        };
      });

      onFileSelect(multiple ? newFiles : newFiles[0]);
    }
  };

  // Handle file drop event
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const files = multiple ? Array.from(event.dataTransfer.files) : [event.dataTransfer.files[0]];
      setSelectedFiles(files);
      processFiles(files);
    }
  };
  
  // Handle drag events
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  // Process the files and call onFileSelect
  const processFiles = (files) => {
    if (files && files.length > 0) {
      const newFiles = files.map(file => {
        const fileType = (file.type || '').split('/')[0];
        const fileExtension = (file.name || '').split('.').pop().toLowerCase();

        let type = 'pdf';  // Default document-like
        if (fileType === 'image') type = 'image';
        else if (fileType === 'video') type = 'video';
        else if (['pdf'].includes(fileExtension)) type = 'pdf';
        else if (['doc', 'docx'].includes(fileExtension)) type = 'docx';
        else if (['ppt', 'pptx'].includes(fileExtension)) type = 'pptx';
        else if (['xls', 'xlsx'].includes(fileExtension)) type = 'xlsx';
        else if (['zip', 'rar', '7z'].includes(fileExtension)) type = 'zip';

        return {
          name: file.name,
          type: type,
          date: new Date().toISOString()?.split('T')[0],
          file: file
        };
      });

      onFileSelect(multiple ? newFiles : newFiles[0]);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = multiple ? Array.from(event.target.files) : [event.target.files[0]];
      setSelectedFiles(files);
      processFiles(files);
    }
  };

  const handleDeleteFile = (fileToDelete) => {
    console.log('Deleting file:', fileToDelete);
    if (multiple && Array.isArray(selectedFiles)) {
      // Use the ID for reliable identification
      const updatedFiles = selectedFiles.filter((file, index) => {
        const fileId = file.id || `file-${index}-${file.name}`;
        return fileId !== fileToDelete.id;
      });
      console.log('Updated files:', updatedFiles);
      setSelectedFiles(updatedFiles.length > 0 ? updatedFiles : []);
    } else {
      setSelectedFiles([]);
    }
  };

  // Create file objects for the FileList component
  const fileListItems = Array.isArray(selectedFiles) && selectedFiles.length > 0 
    ? selectedFiles.map((file, index) => {
        const typeStr = (file.type || '').toLowerCase();
        const nameStr = (file.name || '').toLowerCase();
        let mappedType = 'folder';
        if (typeStr.startsWith('image') || /(\.png|\.jpg|\.jpeg|\.gif|\.webp)$/.test(nameStr)) mappedType = 'image';
        else if (typeStr.startsWith('video') || /(\.mp4|\.mov|\.avi|\.webm)$/.test(nameStr)) mappedType = 'video';
        else if (/\.pdf$/.test(nameStr)) mappedType = 'pdf';
        else if (/(\.docx?|\.pptx?|\.xlsx?)$/.test(nameStr)) {
          if (/\.docx?$/.test(nameStr)) mappedType = 'docx';
          else if (/\.pptx?$/.test(nameStr)) mappedType = 'pptx';
          else if (/\.xlsx?$/.test(nameStr)) mappedType = 'xlsx';
        } else if (/(\.zip|\.rar|\.7z)$/.test(nameStr)) mappedType = 'zip';

        // If the upstream mapper already set a known type, prefer it
        if (['image','video','pdf','docx','pptx','xlsx','zip','folder'].includes(file.type)) {
          mappedType = file.type;
        }

        return {
          id: file.id || `file-${index}-${file.name}`,
          name: file.name,
          type: mappedType,
          date: new Date().toLocaleDateString(),
          originalFile: file
        };
      })
    : [];

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Required notification when no file is uploaded - only show if isRequired is true */}
      {isRequired && (!selectedFiles || selectedFiles.length === 0) && (
        <Alert 
          severity="info" 
          icon={customIcon || <AttachFileIcon fontSize="inherit" />}
          sx={{ width: '100%', mb: 2 }}
        >
          {requiredMessage || translations.file_upload_required}
        </Alert>
      )}

      {/* Drag and Drop Area with Browse Button - Always visible */}
      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        elevation={0}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          padding: '2rem',
          border: '2px dashed #cccccc',
          borderRadius: '16px',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: colors.primary[400],
            backgroundColor: alpha(colors.primary[400], 0.05)
          }
        }}
      >
        <UploadIcon>
          <CloudUploadOutlinedIcon color="primary" sx={{ fontSize: 60 }} />
        </UploadIcon>

        <Typography
          variant="h6"
          color="text.primary"
          sx={{
            fontWeight: 600,
            mb: 1
          }}
        >
          {label || translations.file_upload_label}
        </Typography>

        <Typography 
          variant="body1" 
          color="primary" 
          align="center" 
          sx={{ 
            mb: 2,
            fontWeight: 500,
            color: colors.primary[500]
          }}
        >
          {description || translations.file_upload_description}
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center"
          sx={{ mb: 1 }}
        >
          {acceptedFormats || translations.file_upload_accepted_formats}
        </Typography>

        <Button
          component="label"
          variant="contained"
          disableElevation
          sx={{
            bgcolor: colors.primary[500],
            color: colors.grey[100],
            borderRadius: '24px',
            px: 4,
            py: 1,
            mt: 1,
            '&:hover': {
              bgcolor: colors.primary[600]
            }
          }}
        >
          {browseButtonLabel || "Upload"}
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileChange}
            multiple={multiple}
          />
        </Button>
      </Paper>

      {/* Display uploaded files as list items if they exist */}
      {selectedFiles && selectedFiles.length > 0 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <FileList 
            files={fileListItems} 
            showDelete={true} 
            onDelete={handleDeleteFile} 
          />
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;